// Bilder (Ersatzteile, Geräte, Kundenlogos, Endkunden-Logos) sparsam laden und offline bereithalten.
//
// Prinzip: Jedes Bild hat eine feste Adresse mit Version (?v=<Dateiname>, ändert sich bei jedem Upload)
// und Größe (?w=640, der Server liefert eine verkleinerte WebP-Fassung). Der Service Worker speichert
// es beim ersten Laden im Cache „api-images-v1“ und liefert es danach ohne Netz aus. Das Anmelde-Token
// in der Adresse wird für den Cache-Schlüssel ignoriert. Neu geladen wird nur, wenn sich die Version ändert.
//
// Diese Logik ist bewusst ohne Abhängigkeiten (getestet in imageCache.test.js);
// public/sw.js enthält dieselbe Schlüssel-Regel.

export const IMAGE_CACHE = 'api-images-v1';
export const IMAGE_WIDTH = 640;

const PATHS = {
  part: (id) => `/parts/${id}/image`,
  device: (id) => `/devices/${id}/image`,
  customer: (id) => `/customers/${id}/image`,
  subclient: (id) => `/customers/subclients/${id}/logo`
};

/** Adresse eines Bildes; ohne Version wird es nicht dauerhaft gecacht */
export function buildImageUrl(base, kind, id, version, token, width = IMAGE_WIDTH) {
  const params = new URLSearchParams();
  if (version) params.set('v', version);
  if (width) params.set('w', String(width));
  if (token) params.set('token', token);
  return `${base}${PATHS[kind](id)}?${params}`;
}

/** Cache-Schlüssel: Adresse ohne Token (das Token ändert sich bei jeder Anmeldung) */
export function imageCacheKey(url, origin = globalThis.location?.origin || 'http://localhost') {
  const u = new URL(url, origin);
  u.searchParams.delete('token');
  return u.toString();
}

// ── Vorladen: immer · nicht über Mobilfunk · nie ──
export const PREFETCH_POLICIES = ['always', 'no-cellular', 'never'];
const POLICY_KEY = 'btmx.prefetch';

export function getPrefetchPolicy() {
  try {
    const value = localStorage.getItem(POLICY_KEY);
    return PREFETCH_POLICIES.includes(value) ? value : 'no-cellular';
  } catch {
    return 'no-cellular';
  }
}

export function setPrefetchPolicy(policy) {
  if (!PREFETCH_POLICIES.includes(policy)) return;
  try { localStorage.setItem(POLICY_KEY, policy); } catch { /* Speicher gesperrt */ }
}

/**
 * Darf jetzt vorgeladen werden? (Angezeigte Bilder werden unabhängig davon immer einmal geladen.)
 * Datensparmodus des Geräts hat Vorrang. Ist die Verbindungsart unbekannt (z. B. iPhone),
 * gilt „nicht über Mobilfunk“ als erlaubt.
 */
export function prefetchAllowed(policy, connection) {
  if (policy === 'never') return false;
  if (connection?.saveData) return false;
  if (policy === 'no-cellular' && connection?.type === 'cellular') return false;
  return true;
}

/**
 * Sollen fehlende Ersatzteilbilder auch bei Ansicht nicht nachgeladen werden?
 * Im Mobilfunk/Datensparmodus nur, wenn "Immer" gewählt ist.
 */
export function remoteImagesBlocked(policy, connection) {
  if (policy === 'always') return false;
  return connection?.type === 'cellular' || Boolean(connection?.saveData);
}

export function canLoadRemotePartImages() {
  if (typeof navigator === 'undefined' || !navigator.onLine) return false;
  return !remoteImagesBlocked(getPrefetchPolicy(), navigator.connection);
}

export function canPrefetchNow() {
  if (typeof navigator === 'undefined' || !navigator.onLine) return false;
  return prefetchAllowed(getPrefetchPolicy(), navigator.connection);
}

/** Liegt das Bild bereits im Offline-Cache? */
export async function isImageCached(url) {
  if (typeof caches === 'undefined' || !url) return false;
  try {
    const cache = await caches.open(IMAGE_CACHE);
    return Boolean(await cache.match(imageCacheKey(url)));
  } catch {
    return false;
  }
}

/**
 * Lädt fehlende Bilder vor. Der Service Worker legt sie dabei in den Cache – ohne zweite Kopie.
 * @returns {Promise<{total:number, missing:number, loaded:number, skipped:string|null}>}
 */
export async function prefetchImages(urls, { onStart, onProgress, force = false } = {}) {
  const unique = [...new Set(urls.filter(Boolean))];
  if (!force && !canPrefetchNow()) return { total: unique.length, missing: 0, loaded: 0, skipped: 'policy' };
  if (!navigator.serviceWorker?.controller) return { total: unique.length, missing: 0, loaded: 0, skipped: 'no-sw' };

  const missing = [];
  for (const url of unique) {
    if (!(await isImageCached(url))) missing.push(url);
  }
  if (missing.length === 0) return { total: unique.length, missing: 0, loaded: 0, skipped: null };

  onStart?.(missing.length);
  let loaded = 0;
  const BATCH = 4;
  for (let i = 0; i < missing.length; i += BATCH) {
    if (!force && !canPrefetchNow()) break; // z. B. WLAN verlassen
    await Promise.all(missing.slice(i, i + BATCH).map(async (url) => {
      try {
        const response = await fetch(url, { credentials: 'same-origin' });
        if (response.ok) {
          await response.arrayBuffer(); // vollständig laden, damit der Service Worker speichert
          loaded++;
        }
      } catch { /* einzelnes Bild nicht kritisch */ }
    }));
    onProgress?.(loaded, missing.length);
    if (i + BATCH < missing.length) await new Promise((r) => setTimeout(r, 250));
  }
  return { total: unique.length, missing: missing.length, loaded, skipped: null };
}

/** Alle Katalogbilder (Teile, Geräte, Kunden) vorladen und den Fortschritt als Events melden */
export async function prefetchCatalogImages({ parts = [], devices = [], customers = [] }, base, token, options = {}) {
  if (!token) return { total: 0, missing: 0, loaded: 0, skipped: 'no-token' };
  const urls = [
    ...parts.filter((p) => p.image_filename).map((p) => buildImageUrl(base, 'part', p.id, p.image_filename, token)),
    ...devices.filter((d) => d.image_filename).map((d) => buildImageUrl(base, 'device', d.id, d.image_filename, token)),
    ...customers.filter((c) => c.image_filename).map((c) => buildImageUrl(base, 'customer', c.id, c.image_filename, token))
  ];
  const emit = (name, detail) => window.dispatchEvent(new CustomEvent(name, { detail }));
  const result = await prefetchImages(urls, {
    ...options,
    onStart: (total) => emit('image-download-start', { total }),
    onProgress: (current, total) => emit('image-download-progress', { current, total, percentage: Math.round((current / total) * 100) })
  });
  if (result.missing > 0) emit('image-download-complete', { downloaded: result.loaded, total: result.missing });
  return result;
}

/** Wie viele Katalogbilder sind offline verfügbar? */
export async function getCatalogImageStatus({ parts = [], devices = [], customers = [] }, base, token) {
  const items = [
    ...parts.filter((p) => p.image_filename).map((p) => buildImageUrl(base, 'part', p.id, p.image_filename, token)),
    ...devices.filter((d) => d.image_filename).map((d) => buildImageUrl(base, 'device', d.id, d.image_filename, token)),
    ...customers.filter((c) => c.image_filename).map((c) => buildImageUrl(base, 'customer', c.id, c.image_filename, token))
  ];
  let cached = 0;
  for (const url of items) if (await isImageCached(url)) cached++;
  return { total: items.length, cached };
}

export async function clearImageCacheStorage() {
  if (typeof caches !== 'undefined') await caches.delete(IMAGE_CACHE);
}
